import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Youtube, ArrowRight, Award, Users, ShieldCheck } from 'lucide-react';

interface Project {
  id: number;
  title: string;
  description: string;
  technologies: string[];
  image: string;
  liveUrl?: string;
  featured: boolean;
  category?: 'Leadership' | 'Product' | 'Partnership';
}

const projects: Project[] = [
  {
    id: 1,
    title: "KubeCon Talk: Unlocking Advanced Capabilities with OTel",
    description: "Co-presented with AWS at KubeCon on leveraging OpenTelemetry to optimize observability in complex distributed systems. Focused on advanced processing capabilities and strategic implementation patterns for enterprise scale.",
    technologies: ["KubeCon", "OpenTelemetry", "Public Speaking", "AWS Partnership", "Kubernetes"],
    image: "https://img.youtube.com/vi/-VM1jLWusz8/maxresdefault.jpg",
    liveUrl: "https://www.youtube.com/watch?v=-VM1jLWusz8",
    featured: true,
    category: 'Leadership'
  },
  {
    id: 2,
    title: "Splunk .conf: Strategic Observability Initiative (2022-2024)",
    description: "Strategic lead and featured speaker for observability sessions at Splunk's premier annual conference. Focused on 'Observability at Scale', 'OpenTelemetry Adoption', and 'Streamlining Data Ingestion' for global enterprise customers.",
    technologies: [".conf", "Thought Leadership", "OpenTelemetry", "Data Ingestion", "Observability"],
    image: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800&q=80",
    liveUrl: "https://conf.splunk.com/watch/conf-online.html?search=aunsh%20chaudhari",
    featured: true,
    category: 'Leadership'
  },
  {
    id: 3,
    title: "Expert Interview: The Future of Observability",
    description: "In-depth technical interview at KubeCon 2024 discussing the evolution of OpenTelemetry, zero-touch observability, and the critical role of open standards in cloud-native ecosystems.",
    technologies: ["Thought Leadership", "OpenTelemetry", "KubeCon 2024", "Expert Interview"],
    image: "https://img.youtube.com/vi/y70-fBMpVXM/maxresdefault.jpg",
    liveUrl: "https://www.youtube.com/watch?v=y70-fBMpVXM&t=2s",
    featured: true,
    category: 'Leadership'
  },
  {
    id: 4,
    title: "AWS Partnership: Fargate & Graviton2 Observability",
    description: "Collaborated with Amazon to launch unified observability for AWS Fargate deployments on Graviton2. Authored the strategic technical guide for metrics, traces, and logs collection in real-time.",
    technologies: ["AWS Fargate", "Graviton2", "Splunk", "Strategic Partnership", "Product Strategy"],
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80",
    liveUrl: "https://www.splunk.com/en_us/blog/observability/observability-for-aws-fargate-deployments-powered-by-graviton2-processors.html",
    featured: true,
    category: 'Partnership'
  },
  {
    id: 5,
    title: "OpenTelemetry Strategy & Growth Initiative",
    description: "Leading the industry-wide transition from legacy agents to OpenTelemetry. Authored key strategies for SignalFx Smart Agent migration and launched the Splunk Add-on for OTel Collector.",
    technologies: ["OpenTelemetry", "Product Strategy", "Migration Leadership", "Splunk Collector"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80",
    liveUrl: "https://www.splunk.com/en_us/blog/author/achaudhari.html",
    featured: true,
    category: 'Product'
  },
  {
    id: 6,
    title: "Patent Innovation: Entity Discovery & Inventory",
    description: "Co-inventor of patented technology for 'Performing Iterative Entity Discovery and Instrumentation'. Innovations directly improved customer Time To Value by 67% across enterprise environments.",
    technologies: ["Patent Development", "Innovation Strategy", "Entity Discovery", "Instrumentation"],
    image: "https://images.unsplash.com/photo-1589254065878-42c9da997008?w=800&q=80",
    liveUrl: "https://patents.google.com/?inventor=Aunsh+Chaudhari",
    featured: true,
    category: 'Product'
  },
  {
    id: 7,
    title: "Charles - Intelligent Chatbot",
    description: "Won Best User Experience award for 'Charles - Intelligent Chatbot' at GE Aviation Hackathon Challenge. Developed an innovative chatbot solution demonstrating expertise in AI/ML and user experience design.",
    technologies: ["AI/ML", "Chatbot", "User Experience", "Hackathon", "Innovation"],
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop",
    featured: false,
    category: 'Product'
  }
];

const Projects: React.FC = () => {
  return (
    <section id="projects" className="section-padding bg-gray-50">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Strategic Initiatives & Industry Leadership
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Highlighting key product partnerships with leaders like AWS, 
            strategic technical innovations, and three years of thought leadership at industry-leading conferences.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {projects.filter(project => project.featured).map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-10"></div>
                {project.category && (
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-primary-700 text-xs font-bold rounded-full shadow-sm flex items-center gap-1.5 uppercase tracking-wider">
                      {project.category === 'Partnership' && <Users className="w-3.5 h-3.5" />}
                      {project.category === 'Leadership' && <Award className="w-3.5 h-3.5" />}
                      {project.category === 'Product' && <ShieldCheck className="w-3.5 h-3.5" />}
                      {project.category}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {project.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {project.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-3 py-1 bg-primary-50 text-primary-700 text-xs font-semibold rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                
                <div className="mt-auto">
                  {project.liveUrl && (
                    <motion.a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05, x: 5 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center gap-2 text-primary-600 font-bold hover:text-primary-700 transition-all"
                    >
                      {project.liveUrl.includes('youtube.com') || project.liveUrl.includes('conf.splunk.com') ? (
                        <>Watch Sessions <Youtube className="w-4 h-4" /></>
                      ) : (
                        <>Read Blog/Patent <ExternalLink className="w-4 h-4" /></>
                      )}
                    </motion.a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* LinkedIn CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <motion.a
            href="https://www.linkedin.com/in/aunsh-chaudhari/"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-primary inline-flex items-center gap-2"
          >
            Connect for Future Partnerships
            <ArrowRight className="w-4 h-4" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
